import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GlowCard } from "@/components/ui/spotlight-card";
import { ShineBorder } from "@/components/ui/shine-border";
import { clickCta } from "@/lib/analytics";
import { Marquee } from "./pricing/Marquee";
import { REALITY_ITEMS, REALITY_ITEMS_2 } from "./PricingSection.data";
import { problems } from "./ProblemSection.data";

export function ProblemSection({ onCtaClick }: { onCtaClick?: () => void } = {}) {
  return (
    <section
      id="problem"
      className="relative isolate overflow-visible bg-background -mt-6 py-10 pb-8 sm:-mt-8 sm:py-14 sm:pb-10"
    >

      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at center, color-mix(in oklab, var(--brand-blue) 10%, transparent), transparent 60%)",
        }}
      />

      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto -mt-3 max-w-none text-center sm:-mt-4">
         <span className="inline-block rounded-full bg-brand-red/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-brand-red">
            The Problem
          </span>
          <h2 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:whitespace-nowrap sm:text-5xl lg:text-5xl">
            The web design industry is{" "}
            <span style={{ color: "#ff5251" }}>broken.</span>
          </h2>
          <p className="mx-auto mt-5 text-base text-muted-foreground sm:whitespace-nowrap sm:text-lg">
            Business owners are getting ripped off every single day. Here's what we're fighting against.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {problems.map((p) => (
            <Card
              key={p.title}
              className="group relative overflow-hidden border-border/60 bg-card/60 backdrop-blur transition-colors hover:border-brand-red/40"
            >
              <CardContent className="flex h-full flex-col p-6">
                <div className="relative mb-5 flex h-32 items-center justify-center overflow-hidden rounded-xl border border-border/50 bg-background/40">
                  {p.visual}
                </div>
                <h3 className="text-lg font-semibold text-foreground">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-10 sm:mt-14">
          <span className="inline-block rounded-full bg-brand-red/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-brand-red">
            The Reality
          </span>
          <h2 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl">
            Most businesses pay
            <br />
            <span style={{ color: "#ff5251" }}>$5,000-$8,000</span>
            <span className="hidden sm:inline"> for a website.</span>
            <br className="sm:hidden" />
            <span className="sm:hidden">for a website.</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground">
            And this is what they typically<br className="sm:hidden" /> get for that money:
          </p>
          <div className="mt-10 sm:mt-14">
            <Marquee items={REALITY_ITEMS} variant="red" />
          </div>
          <div className="mt-6">
            <Marquee items={REALITY_ITEMS_2} variant="red" reverse />
          </div>
        </div>

        <ShineBorder
          borderRadius={16}
          borderWidth={2}
          duration={10}
          color={["#3b82f6", "#a855f7", "#ec4899"]}
          className="mt-14 sm:mt-16"
        >
          <GlowCard glowColor="blue" disableInnerGlow customSize className="relative w-full">
            <div className="relative flex flex-col items-center gap-4 px-3 py-10 text-center sm:px-14 sm:py-20">
              <h3 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl">
                So we built{" "}
                <span className="text-gradient-cycle">the opposite.</span>
              </h3>
              <p className="max-w-4xl text-pretty text-base text-muted-foreground sm:text-lg">
                You work directly with us on a simple monthly plan, and get a premium
                <br className="hidden sm:block" />
                site without the agency markup, the months of waiting, or the giant upfront bill.
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              <button
                  type="button"
                  onClick={() => {
                    clickCta("Get a Free Mockup", "problem_section");
                    onCtaClick?.();
                  }}
                  className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                >
                  Get a Free Mockup
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 px-8 rounded-full border-brand-blue text-brand-blue hover:bg-brand-blue/10 font-semibold"
                >
                  <a href="#pricing" onClick={() => clickCta("See Plans", "problem_section")}>See Plans</a>
                </Button>
              </div>
            </div>
          </GlowCard>
        </ShineBorder>
      </div>
    </section>
  );
}
